'use client';

import React from 'react';
import Link from 'next/link';
import {
  Bot,
  Building,
  CalendarDays,
  Check,
  Headphones,
  LayoutDashboard,
  type LucideIcon,
  MessageCircle,
  Minus,
  Scissors,
  Smartphone,
  User,
  Users,
} from 'lucide-react';
import type { BillingCycle, BillingPlan, PlanFeature } from '@/lib/api';
import { usePlatformLabelResolver } from '@/lib/platform-labels';
import { toneChip } from '@/lib/tones';
import { buildPlanCycleView } from './pricing-model';

function formatBRL(cents: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(cents / 100);
}

/** Quebra um valor em centavos em { reais, centavos } para o preco tabular. */
function priceParts(cents: number): { reais: string; centavos: string } {
  const reais = Math.floor(cents / 100);
  const centavos = String(cents % 100).padStart(2, '0');
  return { reais: new Intl.NumberFormat('pt-BR').format(reais), centavos };
}

/** Chave + fallback do rotulo de parcelamento conforme o ciclo longo. */
function installmentsKey(cycle: BillingCycle): { key: string; fallback: string } | null {
  if (cycle === 'SEMIANNUAL')
    return { key: 'pricing.installments.semiannual', fallback: 'em até 6x sem juros no cartão' };
  if (cycle === 'ANNUAL')
    return { key: 'pricing.installments.annual', fallback: 'em até 12x sem juros no cartão' };
  return null;
}

/**
 * Icone-titulo do plano, derivado de maxBarbers (dinamico, sem hardcode de
 * code de plano): autonomo -> equipe -> estudio -> rede.
 */
function tierIcon(maxBarbers: number): React.ReactNode {
  if (maxBarbers <= 1) return <User className="w-5 h-5" aria-hidden="true" />;
  if (maxBarbers <= 5) return <Users className="w-5 h-5" aria-hidden="true" />;
  if (maxBarbers <= 15) return <Scissors className="w-5 h-5" aria-hidden="true" />;
  return <Building className="w-5 h-5" aria-hidden="true" />;
}

/** Chave semantica de icone (vinda do CRUD) -> componente lucide. */
const FEATURE_ICONS: Record<string, LucideIcon> = {
  scissors: Scissors,
  barbers: Scissors,
  calendar: CalendarDays,
  appointments: CalendarDays,
  whatsapp: MessageCircle,
  message: MessageCircle,
  bot: Bot,
  ai: Bot,
  smartphone: Smartphone,
  app: Smartphone,
  dashboard: LayoutDashboard,
  panel: LayoutDashboard,
  headphones: Headphones,
  support: Headphones,
  building: Building,
  users: Users,
  user: User,
  check: Check,
};
function featureIcon(name?: string): LucideIcon {
  return (name && FEATURE_ICONS[name]) || Check;
}

function barbersLabel(maxBarbers: number): string {
  if (maxBarbers >= 999) return 'Barbeiros ilimitados';
  if (maxBarbers === 1) return '1 barbeiro';
  return `Até ${maxBarbers} barbeiros`;
}

/**
 * Features do card. Quando o plano traz `features` (persistidas e editaveis no
 * CRUD do Super Admin), usamos elas. Sem isso, derivamos dos campos do plano
 * (barbeiros, agendamentos, flags) - fallback ate o backend popular a coluna.
 */
function resolveFeatures(plan: BillingPlan): PlanFeature[] {
  if (plan.features && plan.features.length > 0) return plan.features;
  return [
    { label: barbersLabel(plan.maxBarbers), included: true, icon: 'barbers' },
    {
      label:
        plan.maxAppointmentsPerMonth >= 999_999
          ? 'Agendamentos ilimitados'
          : `${plan.maxAppointmentsPerMonth.toLocaleString('pt-BR')} agendamentos/mês`,
      included: true,
      icon: 'appointments',
    },
    { label: 'WhatsApp Business integrado', included: plan.whatsappEnabled, icon: 'whatsapp' },
    { label: 'Chatbot com inteligência artificial', included: plan.chatbotAiEnabled, icon: 'bot' },
    { label: 'App de agendamento para seus clientes', included: true, icon: 'smartphone' },
    { label: 'Painel completo + booking público', included: true, icon: 'dashboard' },
    { label: 'Suporte por e-mail', included: true, icon: 'headphones' },
  ];
}

/**
 * Cartao de um plano na vitrine /pricing. Anatomia: ícone-título + nome
 * (centrado verticalmente entre si), descrição abaixo, preço (Poppins tabular,
 * baseline compartilhada), lista de features com ícone semântico alinhado à 1a
 * linha, e CTA reusando as classes do Button do DS. Tudo dinâmico: planos,
 * preços, features e rótulos vêm do backend / PlatformMessage.
 */
export function PlanCard({
  plan,
  cycle,
  popular,
  trialDays,
}: {
  plan: BillingPlan;
  cycle: BillingCycle;
  popular?: boolean;
  trialDays: number;
}) {
  const label = usePlatformLabelResolver();
  const view = buildPlanCycleView(plan, cycle);
  const features = resolveFeatures(plan);

  const installments = installmentsKey(cycle);
  const hasLongCycleData = installments && view.totalCycleCents != null;
  const ctaHref = view.isPaid
    ? `/register?plan=${encodeURIComponent(plan.code)}&cycle=${cycle}`
    : `/register?plan=${encodeURIComponent(plan.code)}`;
  const ctaLabel = view.isPaid ? `Começar ${trialDays} dias grátis` : 'Cadastrar grátis';

  const parts = priceParts(view.displayMonthlyCents ?? 0);

  return (
    <div className={`section-card pricing-card${popular ? ' pricing-card--popular' : ''}`}>
      {popular && <span className="pricing-card__badge">Mais popular</span>}

      <div className="pricing-card__head">
        <span className={`pricing-card__tier ${toneChip('muted')}`}>{tierIcon(plan.maxBarbers)}</span>
        <h3 className="pricing-card__name">{plan.name}</h3>
      </div>
      {plan.description && <p className="pricing-card__desc">{plan.description}</p>}

      <div className="pricing-price-block">
        {!view.isPaid ? (
          <>
            <div className="pricing-price__free">Grátis</div>
            <p className="pricing-price__note">Comece sem pagar nada.</p>
          </>
        ) : (
          <>
            <div className="pricing-price">
              <span className="pricing-price__currency">R$</span>
              <span className="pricing-price__amount">{parts.reais}</span>
              <span className="pricing-price__cents">,{parts.centavos}</span>
              <span className="pricing-price__period">/mês</span>
            </div>
            {view.cycleUnavailable ? (
              <p className="pricing-price__note">
                {label('pricing.cycle.unavailable', 'Disponível apenas no plano mensal')}
              </p>
            ) : hasLongCycleData ? (
              <>
                <p className="pricing-price__note">
                  {view.discountPercent != null && (
                    <span className="pricing-price__off">
                      {label('pricing.discount.badge', `${view.discountPercent}% OFF`, {
                        percent: view.discountPercent,
                      })}
                    </span>
                  )}
                  {view.struckMonthlyCents != null && (
                    <>
                      {' '}
                      <span className="pricing-price__strike">
                        de {formatBRL(view.struckMonthlyCents)}/mês
                      </span>
                    </>
                  )}
                </p>
                <p className="pricing-price__note">
                  {label('pricing.total.label', `Valor Total: ${formatBRL(view.totalCycleCents!)}`, {
                    total: formatBRL(view.totalCycleCents!),
                  })}
                  {' · '}
                  {label(installments!.key, installments!.fallback)}
                </p>
              </>
            ) : (
              <p className="pricing-price__note">
                Cobrança mensal
                <br />
                Cancele quando quiser.
              </p>
            )}
          </>
        )}
      </div>

      <div className="pricing-card__divider" />

      <ul className="pricing-features">
        {features.map((f, i) => {
          const Icon = f.included ? featureIcon(f.icon) : Minus;
          return (
            <li
              key={`${f.label}-${i}`}
              className={`pricing-feature${f.included ? '' : ' pricing-feature--off'}`}
            >
              <Icon className="pricing-feature__icon" aria-hidden="true" />
              <span className="pricing-feature__text">{f.label}</span>
            </li>
          );
        })}
      </ul>

      <Link
        href={ctaHref}
        className={`btn btn--md btn--block pricing-card__cta ${popular ? 'btn--primary' : 'btn--neutral'}`}
      >
        <span>{ctaLabel}</span>
      </Link>
    </div>
  );
}
